import * as React from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ConnectorPicker } from './ConnectorPicker';
import { FileUploadStage } from './FileUploadStage';
import { ConnectedTabularSourceStage } from './ConnectedTabularSourceStage';
import { SourcesInfoPopover } from './HowSourcesWork';
import { useConnectorListState } from '../hooks/useConnectorList';
import type { ConnectorManifest, FolderKind, ImportTarget } from '../connectorRegistry';
import {
  isBuiltInConnectedSourceConnector,
  isBuiltInImportSourceModalConnector,
} from '../services/importTargetSupport';
import { cn } from '@/lib/utils';
import { useFolderAccess } from '@/hooks/useFolderAccess';
import { useFeatureInterestRequest } from '../hooks/useFeatureInterestRequest';
import { useDemoStatus } from '../../../hooks/useAuth';

export interface ImportSourceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folderId: string;
  folderKind: FolderKind;
  /** Required when defaultTarget includes 'diagram'. */
  diagramId?: string;
  diagramType?: string;
  defaultTarget: ImportTarget;
  /** Optional title override (defaults to "Import data"). */
  title?: string;
  /**
   * Count of sources already connected to the folder. Drives cardinality
   * cues in the modal — when > 0, the subtitle reads "{N} already
   * connected · adding another," reinforcing that sources accumulate.
   */
  existingSourcesCount?: number;
  /**
   * Called when the user picks a connector. Return `false` to suppress
   * the built-in file-upload stage and drive the flow yourself — useful
   * for non-file connectors (OAuth, live scans) that need custom UI.
   * Default is `true`: the modal proceeds to its built-in file upload
   * stage for `format='file'` connectors.
   */
  onConnectorSelected?: (manifest: ConnectorManifest) => boolean | void;
  /** Called after a successful apply through the built-in stage. */
  onComplete?: (result: { createdCount: number; updatedCount: number }) => void;
  /**
   * Optional primary next step shown after a successful file import.
   * The import stays complete and durable if the user closes instead.
   */
  completionAction?: {
    label: string;
    onClick: () => void;
    pending?: boolean;
    disabled?: boolean;
  };
  /**
   * Optional connector id to land on directly — skips the picker stage
   * and opens the connector's file upload UI. Used by entry points that
   * are SOP-specific (e.g. the "Import SOP" CTA on the process folder
   * empty state), so the user doesn't have to re-pick a connector
   * they've already implicitly chosen.
  */
  initialConnectorId?: string;
  /** Optional files captured by an outer drop surface. */
  initialFiles?: readonly File[];
  /** Optional filter for entry points that should expose a curated subset. */
  connectorFilter?: (manifest: ConnectorManifest) => boolean;
  /** Compact picker chrome for narrow, first-run import surfaces. */
  compact?: boolean;
  /** Optional label override above the connector picker. */
  pickerLabel?: string;
}

/**
 * Single entry point for every "bring data in" flow in the app.
 *
 * Stages:
 *   1. Connector picker (filtered by folderKind + diagramType + defaultTarget).
 *   2. Connector-specific configuration:
 *      - format='file'  → built-in FileUploadStage (paste/upload → apply
 *        via POST /api/folders/:folderId/records, target=folder|both)
 *      - other formats  → host handles via onConnectorSelected returning
 *        false (OAuth, api-key, live-scan UX lives in the domain).
 *   3. Apply result surfaced via onComplete.
 *
 * See plans/archive/compass/import-surfaces-consolidation-plan-archived.md §Phase A.
 */
export const ImportSourceModal: React.FC<ImportSourceModalProps> = ({
  open,
  onOpenChange,
  folderId,
  folderKind,
  diagramType,
  defaultTarget,
  title = 'Import data',
  existingSourcesCount,
  onConnectorSelected,
  onComplete,
  completionAction,
  initialConnectorId,
  initialFiles,
  connectorFilter,
  compact = false,
  pickerLabel = 'Pick a source',
}) => {
  const {
    connectors: availableConnectors,
    isLoading: connectorsLoading,
  } = useConnectorListState({ folderKind, diagramType, target: defaultTarget });
  const folderAccess = useFolderAccess(open ? folderId : undefined);
  const connectors = React.useMemo(
    () => {
      const filtered = connectorFilter
        ? availableConnectors.filter(connectorFilter)
        : availableConnectors;
      return filtered.filter(
        (manifest) =>
          isBuiltInImportSourceModalConnector(manifest) ||
          Boolean(onConnectorSelected),
      );
    },
    [availableConnectors, connectorFilter, onConnectorSelected],
  );
  const [selected, setSelected] = React.useState<ConnectorManifest | undefined>();
  const [contactSalesConnector, setContactSalesConnector] =
    React.useState<ConnectorManifest | undefined>();
  const [contactSalesError, setContactSalesError] = React.useState<string | undefined>();
  const [contactEmail, setContactEmail] = React.useState('');
  const [contactSalesSubmitted, setContactSalesSubmitted] = React.useState(false);
  const isDemoUser = useDemoStatus()?.toUpperCase() === 'DEMO';
  const featureInterest = useFeatureInterestRequest();
  const isContactSalesPending = Boolean(contactSalesConnector?.salesInterestId) && featureInterest.isPending;
  const hasExisting = (existingSourcesCount ?? 0) > 0;
  const emptyDescription = React.useMemo(
    () => sourceModalDescription(folderKind),
    [folderKind],
  );

  // When the host pre-picks a connector (e.g. "Import SOP" on the
  // process empty state), land directly on its file upload stage. We
  // re-evaluate whenever the modal opens so subsequent opens of the
  // same modal instance honor a new initialConnectorId, and reset
  // `selected` on close so the picker reappears next time without
  // explicit cleanup from the host.
  React.useEffect(() => {
    if (!open) {
      setSelected(undefined);
      setContactSalesConnector(undefined);
      setContactSalesError(undefined);
      setContactEmail('');
      setContactSalesSubmitted(false);
      return;
    }
    if (!initialConnectorId) return;
    const match = connectors.find((c) => c.id === initialConnectorId);
    if (match && match.format === 'file') setSelected(match);
  }, [open, initialConnectorId, connectors]);

  React.useEffect(() => {
    if (!selected) return;
    if (connectors.some((connector) => connector.id === selected.id)) return;
    setSelected(undefined);
  }, [connectors, selected]);

  const handleSelect = React.useCallback(
    (manifest: ConnectorManifest) => {
      // Host opts in to custom handling by returning `false`.
      const hostHandled = onConnectorSelected?.(manifest);
      if (hostHandled === false) return;

      // File and supported connected sources use built-in folder stages.
      if (
        manifest.format !== 'file' &&
        !isBuiltInConnectedSourceConnector(manifest)
      ) return;

      setSelected(manifest);
    },
    [onConnectorSelected],
  );

  const handleContactSales = React.useCallback(async (manifest: ConnectorManifest, email?: string) => {
    if (!manifest.salesInterestId) return;
    setContactSalesError(undefined);
    setContactSalesConnector(manifest);
    if (isDemoUser && email === undefined) return;
    try {
      await featureInterest.mutateAsync({ interestId: manifest.salesInterestId, contactEmail: email });
      setContactSalesSubmitted(true);
    } catch (error) {
      setContactSalesError(error instanceof Error ? error.message : 'We could not send your request. Please try again.');
    }
  }, [featureInterest, isDemoUser]);

  const handleBack = React.useCallback(() => setSelected(undefined), []);

  const handleClose = React.useCallback(() => {
    setSelected(undefined);
    setContactSalesConnector(undefined);
    setContactSalesError(undefined);
    setContactEmail('');
    setContactSalesSubmitted(false);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleAddAnother = React.useCallback(() => {
    setSelected(undefined);
  }, []);

  const handleComplete = React.useCallback(
    (result: { createdCount: number; updatedCount: number }) => {
      onComplete?.(result);
    },
    [onComplete],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)] gap-0 overflow-hidden rounded-[14px] border-[color:var(--color-ink-100)] bg-white p-0 shadow-[0_24px_64px_rgba(13,42,77,0.18)]',
          compact
            ? 'max-h-[min(560px,calc(100vh-2rem))] sm:max-w-lg'
            : 'max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] sm:max-w-5xl',
        )}
      >
        <DialogHeader
          className={cn(
            'shrink-0 border-b border-[color:var(--color-ink-100)] bg-white pr-12',
            compact ? 'px-4 py-4' : 'px-6 py-5',
          )}
          data-testid="tid.compass.importSourceModal.header"
        >
          <DialogTitle
            className={cn(
              'font-[var(--font-display)] font-bold tracking-tight text-[color:var(--color-navy-900)]',
              compact ? 'text-base' : 'text-lg',
            )}
          >
            {selected && selected.format !== 'file' ? `Import from ${selected.label}` : title}
          </DialogTitle>
          {hasExisting ? (
            <DialogDescription className="text-xs text-[color:var(--color-ink-500)]">
              {existingSourcesCount} already connected · adding another
            </DialogDescription>
          ) : (
            <DialogDescription className="sr-only">
              {emptyDescription}
            </DialogDescription>
          )}
        </DialogHeader>

        <div
          key={selected?.id ?? 'picker'}
          className={cn(
            'min-h-0 overflow-y-auto',
            compact ? 'px-4 py-4' : 'px-6 py-5',
            selected && selected.format !== 'file' ? 'bg-white' : 'bg-[color:var(--color-ink-50)]',
          )}
          data-testid="tid.compass.importSourceModal.body"
        >
          {!selected ? (
            <div className={cn('flex flex-col', compact ? 'gap-4' : 'gap-6')}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    {pickerLabel}
                  </div>
                  <p className="mt-1 max-w-2xl text-sm leading-5 text-muted-foreground">
                    {emptyDescription}
                  </p>
                </div>
                <SourcesInfoPopover />
              </div>
              <ConnectorPicker
                connectors={connectors}
                loading={connectorsLoading}
                selectedId={undefined}
                onSelect={handleSelect}
                onContactSales={handleContactSales}
                contactSalesPendingId={featureInterest.isPending ? featureInterest.variables?.interestId : undefined}
                compact={compact}
              />
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={handleClose}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : selected.format === 'file' ? (
            <FileUploadStage
              manifest={selected}
              folderId={folderId}
              folderKind={folderKind}
              target={defaultTarget}
              onBack={handleBack}
              onCancel={handleClose}
              onComplete={handleComplete}
              onAddAnother={handleAddAnother}
              completionAction={completionAction}
              initialFiles={initialFiles}
              canGrantOwnerImportAccess={folderAccess.data?.role === 'owner'}
            />
          ) : (
            <ConnectedTabularSourceStage
              manifest={selected}
              folderId={folderId}
              folderKind={folderKind}
              target={defaultTarget}
              onBack={handleBack}
              onCancel={handleClose}
              onComplete={handleComplete}
              onAddAnother={handleAddAnother}
              completionAction={completionAction}
            />
          )}
        </div>
      </DialogContent>
      <Dialog
        open={Boolean(contactSalesConnector)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !isContactSalesPending) {
            setContactSalesConnector(undefined);
            setContactSalesError(undefined);
            setContactEmail('');
            setContactSalesSubmitted(false);
          }
        }}
      >
        <DialogContent showCloseButton={false} className="gap-5 p-6 sm:max-w-md">
          <DialogHeader className="items-center gap-3 text-center sm:text-center">
            <img
              src="/assets/images/creately-logo-mark-light.svg"
              alt="Creately"
              className="h-6 w-auto dark:hidden"
            />
            <img
              src="/assets/images/creately-logo-mark-dark.svg"
              alt="Creately"
              className="hidden h-6 w-auto dark:block"
            />
            <DialogTitle>{contactSalesError ? 'Request not sent' : isDemoUser && !contactSalesSubmitted ? 'How can we reach you?' : 'We’ll be in touch'}</DialogTitle>
            <DialogDescription className="max-w-xs">
              {contactSalesError
                ? 'Please review the message below.'
                : isDemoUser && !contactSalesSubmitted
                ? 'Enter the email address you would like our team to contact.'
                : 'Thank you for reaching out! We’ve received your request, and one of our experts will be in touch with you shortly.'}
            </DialogDescription>
            {contactSalesError ? (
              <DialogDescription role="alert" className="text-destructive">
                {contactSalesError}
              </DialogDescription>
            ) : null}
          </DialogHeader>
          {isDemoUser && !contactSalesSubmitted && contactSalesConnector ? (
            <form
              className="flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                void handleContactSales(contactSalesConnector, contactEmail.trim());
              }}
            >
              <label htmlFor="contact-sales-email" className="text-sm font-medium">Contact email</label>
              <input
                id="contact-sales-email"
                data-testid="tid.atlas.contactSales.email"
                type="email"
                required
                autoFocus
                autoComplete="email"
                value={contactEmail}
                onChange={(event) => setContactEmail(event.target.value)}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              />
              <Button type="submit" disabled={isContactSalesPending}>
                {isContactSalesPending ? 'Sending request…' : 'Submit'}
              </Button>
            </form>
          ) : (
          <div className="flex justify-center">
            {contactSalesError && contactSalesConnector ? (
              <Button onClick={() => handleContactSales(contactSalesConnector)}>Retry</Button>
            ) : isContactSalesPending ? (
              <Button disabled>
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                Sending request…
              </Button>
            ) : !isContactSalesPending ? (
              <Button onClick={() => setContactSalesConnector(undefined)}>
                Close
              </Button>
            ) : null}
          </div>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

function sourceModalDescription(folderKind: FolderKind): string {
  if (folderKind === 'genogram') {
    return 'Pick case notes, transcripts, referrals, or records to feed this genogram workspace.';
  }
  if (folderKind === 'orgchart') {
    return 'Pick a source to feed people, departments, and organizational views.';
  }
  if (folderKind === 'process') {
    return 'Pick a source to feed process inventory, review, and repository views.';
  }
  if (folderKind === 'network') {
    return 'Pick a source to feed infrastructure inventory and architecture views.';
  }
  return 'Pick a source to feed this folder workspace.';
}
